import React, { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { FiSearch, FiX, FiHash, FiCalendar, FiMail, FiUser, FiAlertCircle, FiEye } from "react-icons/fi";
import { FaEnvelope, FaInbox, FaCheckCircle, FaReply } from "react-icons/fa";
import { BsThreeDotsVertical } from "react-icons/bs";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";
import "../../../assets/css/superadmin/contactform/ContactForm.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "#FDB714", bg: "#FFF8E6" },
  { value: "opened", label: "Opened", color: "#8B5CF6", bg: "#F3F0FF" },
  { value: "replied", label: "Replied", color: "#10B981", bg: "#ECFDF5" },
  { value: "resolved", label: "Resolved", color: "#3B82F6", bg: "#EFF6FF" },
  { value: "closed", label: "Closed", color: "#6B7280", bg: "#F3F4F6" },
];

const getInitials = (name) => {
  if (!name) return "NA";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((s) => s.value === (status || "new")) || STATUS_OPTIONS[0];

const AdminContactForm = () => {
  const { baseUrl, baseUrlForFile , authToken} = useContext(AppContext);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // ── FETCH ────────────────────────────────────────────────────────
  const viewAllContacts = () => {
    setLoading(true);
    axios.get(`${baseUrl}/superadmin/contactus/viewAllContacts`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then(({ data }) => {
        console.log("Contact records:", data.contact); 
        if (data.success) setRecords(data.contact || []);
        else setRecords([]);
        setLoading(false);
      })
      .catch(() => { setRecords([]); setLoading(false); });
  };

  useEffect(() => { viewAllContacts(); }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".acf-dropdown-wrapper")) setActiveDropdown(null);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // ── STATS ────────────────────────────────────────────────────────
  const total = records.length;
  const newCount = records.filter((r) => (r.status || "new") === "new").length;
  const replied = records.filter((r) => r.status === "replied").length;
  const resolved = records.filter((r) => r.status === "resolved").length;

  const statsData = [
    { icon: <FaInbox />, value: total, label: "Total Inquiries", color: "#FDB714", bg: "#FFF8E6" },
    { icon: <FaEnvelope />, value: newCount, label: "New Messages", color: "#FDB714", bg: "#FFF8E6" },
    { icon: <FaReply />, value: replied, label: "Replied", color: "#FDB714", bg: "#FFF8E6" },
    { icon: <FaCheckCircle />, value: resolved, label: "Resolved", color: "#FDB714", bg: "#FFF8E6" },
  ];

  // ── FILTER ───────────────────────────────────────────────────────
  const filtered = records.filter((r) =>
    (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (r.subject || "").toLowerCase().includes(search.toLowerCase())
  );

  // ── STATUS UPDATE ────────────────────────────────────────────────
  const updateStatus = (id, status) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    if (selectedRecord?.id === id) setSelectedRecord((prev) => ({ ...prev, status }));
  };

  // ── SEND EMAIL ───────────────────────────────────────────────────
  const sendEmail = () => {
    if (!replyText.trim()) {
      Swal.fire({ icon: "warning", title: "Empty!", text: "Please write a reply first." });
      return;
    }
    setSendingEmail(true);
    axios.post(`${baseUrl}/superadmin/contactus/sendReply`, {
      id: selectedRecord.id,
      reply: replyText,
    }, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((res) => {
        setSendingEmail(false);
        if (res.data.success) {
          setReplyText("");
          setShowCompose(false);
          updateStatus(selectedRecord.id, "replied");
          Swal.fire({
            icon: "success", title: "Email Sent!",
            text: "Reply sent successfully.",
            timer: 1800, showConfirmButton: false,
            confirmButtonColor: "#FDB714",
          });
        } else {
          Swal.fire({ icon: "error", title: "Failed", text: res.data.message || "Could not send reply." });
        }
      })
      .catch(() => {
        setSendingEmail(false);
        Swal.fire({ icon: "error", title: "Failed", text: "Something went wrong sending the email." });
      });
  };

  // ── OPEN DETAIL ──────────────────────────────────────────────────
  const openDetail = (record, compose = false) => {
    setSelectedRecord(record);
    setReplyText("");
    setShowCompose(compose);
    setShowModal(true);
    setActiveDropdown(null);
    if ((record.status || "new") === "new") updateStatus(record.id, "opened");
  };

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MasterLayout>
      <div className="acf-page">

        {/* PAGE HEADER */}
        <div className="acf-page-header">
          <h2 className="acf-page-title">Contact Inquiries</h2>
          <p className="acf-page-subtitle">Manage and respond to customer messages</p>
        </div>

        {/* STAT CARDS */}
        <Row className="g-3 mb-4">
          {statsData.map((s, i) => (
            <Col key={i} xl={3} lg={3} md={6} sm={12}>
              <div className="acf-stat-card">
                <div className="acf-stat-icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div className="acf-stat-content">
                  <h3 style={{ color: s.color }}>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* TABLE CARD */}
        <div className="acf-table-card">
          <div className="acf-table-header">
            <h3 className="acf-table-title">All Contact Messages</h3>
            <div className="acf-search-box">
              <FiSearch className="acf-search-icon" />
              <input
                type="text"
                className="acf-search-input"
                placeholder="Search by name, email or subject..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="acf-search-clear" onClick={() => setSearch("")}>
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          {/* TABLE */}
          <div className="acf-table-wrapper">
            <table className="acf-table">
              <thead>
                <tr>
                  <th>CUSTOMER NAME</th>
                  <th>SUBJECT</th>
                  <th>DATE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="acf-empty-row">
                      <div className="acf-loading">
                        <div className="acf-spinner"></div>
                        Loading...
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="acf-empty-row">No records found</td>
                  </tr>
                ) : (
                  filtered.map((record) => {
                    const sm = getStatusMeta(record.status);
                    return (
                      <tr key={record.id}>
                        {/* Customer */}
                        <td>
                          <div className="acf-customer">
                            <div className="acf-avatar">
                              {record.image
                                ? (
                                  <img
                                    src={`${baseUrlForFile}/${record.image}`}
                                    alt={record.name}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )
                                : getInitials(record.name)
                              }
                            </div>
                            <div>
                              <div className="acf-customer-name">{record.name || "N/A"}</div>
                              <div className="acf-customer-email">{record.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Subject */}
                        <td>
                          <div className="acf-subject-title">{record.subject}</div>
                          <div className="acf-subject-preview">{record.message}</div>
                        </td>
                        {/* Date */}
                        <td><span className="acf-date">{formatDate(record.created_at)}</span></td>
                        {/* Status Badge */}
                        <td>
                          <span className="acf-status-badge"
                            style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.color}30` }}>
                            <span className="acf-badge-dot" style={{ background: sm.color }}></span>
                            {(record.status || "new").toUpperCase()}
                          </span>
                        </td>
                        {/* Actions */}
                        <td className="acf-col-action">
                          <div className="acf-action-buttons">


                            <div className="acf-dropdown-wrapper">
                              <button
                                className="acf-dots-btn"
                                onClick={() => setActiveDropdown(activeDropdown === record.id ? null : record.id)}
                              >
                                <BsThreeDotsVertical size={16} />
                              </button>
                              {activeDropdown === record.id && (
                                <div className="acf-dropdown-menu">
                                  <button className="acf-dropdown-item" onClick={() => openDetail(record, true)}>
                                    ✉️ Send Reply
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ══ DETAIL / REPLY MODAL ══ */}
        {showModal && selectedRecord && (() => {
          const sm = getStatusMeta(selectedRecord.status);
          return (
            <div className="acf-modal-overlay" onClick={() => setShowModal(false)}>
              <div className="acf-modal-box" onClick={(e) => e.stopPropagation()}>

                {/* Black Header */}
                <div className="acf-modal-header-dark">
                  <div>
                    <h5 className="acf-modal-header-title">Message Detail</h5>
                    <span className="acf-modal-header-sub">Full breakdown of this message</span>
                  </div>
                  <button className="acf-modal-close-circle" onClick={() => setShowModal(false)}>
                    <FiX />
                  </button>
                </div>

                <div className="acf-modal-body">

                  {/* Status Bar */}
                  <div className="acf-modal-status-bar">
                    <div className="acf-sb-left">
                      <FiHash size={15} color="#FDB714" />
                      <span className="acf-sb-id">{selectedRecord.id}</span>
                      <span className="acf-modal-badge"
                        style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.color}30` }}>
                        <span className="acf-modal-badge-dot" style={{ background: sm.color }}></span>
                        {(selectedRecord.status || "new").toUpperCase()}
                      </span>
                    </div>
                    <div className="acf-sb-date">
                      <FiCalendar size={13} />
                      {formatDate(selectedRecord.created_at)}
                    </div>
                  </div>

                  {/* Section Label */}
                  <p className="acf-section-label"><FiUser size={14} /> Contact Information</p>

                  {/* Info Grid */}
                  <div className="acf-info-grid">
                    <div className="acf-info-item">
                      <label>NAME</label>
                      <span>{selectedRecord.name}</span>
                    </div>
                    <div className="acf-info-item">
                      <label>EMAIL</label>
                      <span>{selectedRecord.email}</span>
                    </div>
                    <div className="acf-info-item" style={{ gridColumn: "1 / -1" }}>
                      <label>SUBJECT</label>
                      <span>{selectedRecord.subject}</span>
                    </div>
                    <div className="acf-info-item" style={{ gridColumn: "1 / -1" }}>
                      <label>FULL MESSAGE</label>
                      <span className="acf-message-text">{selectedRecord.message}</span>
                    </div>
                  </div>

                  {/* Compose Section */}
                  {showCompose && (
                    <>
                      <p className="acf-section-label"><FiMail size={14} /> Reply to {selectedRecord.email}</p>
                      <div className="acf-info-item" style={{ marginBottom: 16 }}>
                        <textarea
                          className="acf-form-textarea"
                          rows={4}
                          placeholder="Write your reply here..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Footer Buttons */}
                <div className="acf-modal-footer-list">
                  {!showCompose ? (
                    <button className="acf-mf-btn acf-mf-reply" onClick={() => setShowCompose(true)}>
                      ✉️ Send Email Reply
                    </button>
                  ) : (
                    <button className="acf-mf-btn acf-mf-reply" onClick={sendEmail} disabled={sendingEmail}>
                      {sendingEmail ? "Sending..." : "✉️ Send Reply"}
                    </button>
                  )}
                  <button className="acf-mf-btn acf-mf-close" onClick={() => setShowModal(false)}>
                    <FiX size={15} /> Close
                  </button>
                </div>

              </div>
            </div>
          );
        })()}

      </div>
    </MasterLayout>
  );
};

export default AdminContactForm;